"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, PhoneCall, ArrowRight, ShieldCheck, FileCheck, Hammer, Clock, Landmark } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "estimates",
  question: "How accurate are initial remodeling estimates?",
  answer: "We begin with preliminary ballpark ranges based on historical square footage and scope in Decatur. Following full site discovery, architectural measurements, and trade partner walk-throughs, we finalize a fixed-price agreement. Your contract outlines specific line-item allowances so there are no unexpected pricing jumps during production.",
  category: "Cost & Budget",
  icon: "file"
}, {
  id: "financing",
  question: "What financing options are available for major renovations?",
  answer: "Most homeowners utilize Home Equity Lines of Credit (HELOC), cash reserves, or specialized residential construction renovation loans. We structure your payment milestones cleanly across confirmed project benchmarks: preliminary deposit, rough-in approvals, cabinetry installation, and final substantial completion sign-off.",
  category: "Financing",
  icon: "landmark"
}, {
  id: "permits",
  question: "How are City of Decatur building permits and historic inspections handled?",
  answer: "Our team manages 100% of municipal submittals with the City of Decatur Building Division, including structural engineering reviews, zoning variances, and Historic Preservation Commission approvals when working in designated neighborhoods like Oakhurst or Downtown Decatur.",
  category: "Permitting",
  icon: "shield"
}, {
  id: "timeline",
  question: "How long does the pre-construction planning phase typically take?",
  answer: "The planning phase generally takes between 4 and 12 weeks depending on the architectural scope. This dedicated period allows us to complete detailed 3D space layouts, order long-lead fixtures and custom cabinetry, and finalize permits before demo crews step foot in your home.",
  category: "Timeline",
  icon: "clock"
}, {
  id: "unforeseen",
  question: "What happens if unforeseen structural issues arise in older Decatur homes?",
  answer: "Many 1920s through 1950s homes in our area carry hidden framing anomalies, settled foundation piers, or legacy plumbing. We always budget a dedicated 5-10% contingency allowance into the plan and present clear, written photographic discovery reports and change orders before undertaking any structural remedies.",
  category: "Craftsmanship",
  icon: "hammer"
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const getFaqIcon = (iconName: string) => {
    switch (iconName) {
      case "landmark":
        return <Landmark className="size-4 text-foreground" />;
      case "shield":
        return <ShieldCheck className="size-4 text-foreground" />;
      case "clock":
        return <Clock className="size-4 text-foreground" />;
      case "hammer":
        return <Hammer className="size-4 text-foreground" />;
      case "file":
      default:
        return <FileCheck className="size-4 text-foreground" />;
    }
  };
  return <section id="process-planning-faq" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border transition-colors duration-300">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center mb-12 sm:mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex justify-center mb-4">
            <Badge variant="outline" className="bg-secondary text-secondary-foreground border-border px-3 py-1 font-sans text-xs tracking-wider uppercase font-semibold rounded-md">
              Planning & Pre-Construction FAQs
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.4,
          delay: 0.08,
          ease: [0.16, 1, 0.3, 1]
        }} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
            Clarity before the first hammer swings
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.4,
          delay: 0.16,
          ease: [0.16, 1, 0.3, 1]
        }} className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Realistic answers about budgeting, City of Decatur permits, timeline milestones, and historic home craftsmanship.
          </motion.p>
        </div>

        {/* FAQ Accordion List */}
        <div className="mx-auto max-w-3xl">
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, index) => <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.4,
            delay: shouldReduceMotion ? 0 : index * 0.06,
            ease: [0.16, 1, 0.3, 1]
          }} className="group">
                <AccordionItem value={item.id} className="bg-card text-card-foreground border border-border rounded-lg px-6 py-1 shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-foreground/30 hover:shadow-md data-[state=open]:border-foreground/40">
                  <AccordionTrigger className="py-5 text-left font-sans text-base sm:text-lg font-semibold text-foreground hover:no-underline flex items-center justify-between gap-4">
                    <span className="flex items-center gap-3">
                      <span className="hidden sm:inline-flex items-center justify-center size-8 rounded-md bg-secondary text-secondary-foreground shrink-0 border border-border/60">
                        {getFaqIcon(item.icon)}
                      </span>
                      <span>{item.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground font-sans text-sm sm:text-base leading-relaxed pt-1 pb-6 border-t border-border/40 mt-1">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-block text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted px-2 py-0.5 rounded">
                        {item.category}
                      </span>
                    </div>
                    <p>{item.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              </motion.div>)}
          </Accordion>

          {/* Bottom Consultation Assistance Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.4,
          delay: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="mt-10 sm:mt-12">
            <Card className="bg-card text-card-foreground border border-border rounded-xl shadow-sm p-6 sm:p-8">
              <CardContent className="p-0 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 text-foreground font-sans font-semibold text-base">
                    <HelpCircle className="size-5 text-foreground shrink-0" />
                    <span>Have specific questions about your home?</span>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans max-w-lg leading-relaxed">
                    Every Decatur home has distinct architectural quirks. Contact our design and construction team to discuss your plans or request a preliminary site visit.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto shrink-0">
                  <Button asChild variant="outline" className="border-border bg-background text-foreground hover:bg-secondary font-sans font-semibold text-sm h-11 px-5 rounded-md">
                    <a href="tel:+14708479076" className="inline-flex items-center justify-center gap-2">
                      <PhoneCall className="size-4 text-foreground" />
                      <span>(470) 847-9076</span>
                    </a>
                  </Button>

                  <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-sans font-semibold text-sm h-11 px-6 rounded-md shadow-sm">
                    <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                      <span>Schedule Discovery</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}